<template>
  <el-row id="build-item" v-bind:class="{ [build.state]: true, summary }" :gutter="0">
    <el-col v-if="summary" :span="24">
      <el-table :data="state" size="mini" class="info" :show-header="false">
        <el-table-column width="26px">
          <template slot-scope="scope"><i :class="scope.row.icon" /></template>
        </el-table-column>
        <el-table-column>
          <template slot-scope="scope">
            <div v-if="scope.$index" v-html="scope.row.text" />

            <Repository
              v-else
              :repository-id="build.repositoryId"
              class="title"
              size="small"
            />
          </template>
        </el-table-column>
      </el-table>
    </el-col>

    <template v-else>
      <el-col :span="10">
        <el-table :data="commit" size="mini" class="info" :show-header="false">
          <el-table-column width="26px">
            <template slot-scope="scope"><i :class="scope.row.icon" /></template>
          </el-table-column>
          <el-table-column>
            <template slot-scope="scope"><div v-html="scope.row.text" /></template>
          </el-table-column>
        </el-table>
      </el-col>

      <el-col :span="8">
        <el-table :data="state" size="mini" class="state" :show-header="false">
          <el-table-column width="26px">
            <template slot-scope="scope"><i :class="scope.row.icon" /></template>
          </el-table-column>
          <el-table-column>
            <template slot-scope="scope"><div v-html="scope.row.text" /></template>
          </el-table-column>
        </el-table>
      </el-col>

      <el-col :span="6">
        <div class="actions">
          <el-button
            type="info"
            icon="el-icon-refresh-right"
            size="small"
            plain
            :disabled="(build.state !== 'passed' && build.state !== 'failed') || restarting"
            @click.prevent="restart"
          >
            Restart build
          </el-button>

          <el-button
            v-if="showLogs"
            :type="type"
            icon="mdi mdi-debug-step-into"
            size="small"
          >
            Display log
          </el-button>
        </div>
      </el-col>
    </template>
  </el-row>

</template>

<script>
import moment from 'moment-timezone'
import Repository from './Repository'

export default {
  props: {
    build: Object,
    summary: {
      type: Boolean,
      default: false
    },
    showLogs: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Repository
  },
  data () {
    return {
      now: null,
      timer: null,
      restarting: false
    }
  },
  created () {
    if (this.build.state === 'running') {
      this.updateTimer()
    }
  },
  computed: {
    icon () {
      let icon = 'el-icon-loading'
      if (this.build.state === 'passed') {
        icon = 'el-icon-circle-check'
      } else if (this.build.state === 'failed') {
        icon = 'el-icon-error'
      }
      return icon
    },
    commit () {
      return [{
        icon: this.icon,
        text: `<h3>${this.build.message.replace(/\s+/, ' ').trim()}</h3>`
      }, {
        icon: 'mdi mdi-source-commit',
        text: `Commit <span class="monospace">${this.build.commitId.substr(-7)}</span>`
      }, {
        icon: 'mdi mdi-source-branch',
        text: `Branch <span class="monospace">${this.build.branch}</span>`
      }, {
        icon: 'mdi mdi-account-circle',
        text: this.build.author.replace(/</, '(').replace(/>/, ')')
      }]
    },
    state () {
      let duration = this.toDuration(this.build.duration)
      if (this.build.state === 'pending') {
        duration = ''
      } else if (this.build.state === 'running') {
        duration = this.toDuration(this.now - this.build.started)
      }
      return [{
        icon: this.summary ? this.icon : '',
        text: `<h3>${this.summary ? this.title : this.build.state}</h3>`
      }, {
        icon: 'mdi mdi-progress-clock',
        text: duration
      }, {
        icon: 'mdi mdi-calendar-clock',
        text: this.toCalendar(this.build.created)
      }]
    },
    type () {
      if (this.build.state === 'running') {
        return 'primary'
      } else if (this.build.state === 'failed') {
        return 'danger'
      } else if (this.build.state === 'passed') {
        return 'success'
      } else {
        return 'info'
      }
    }
  },
  beforeDestroy () {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    restart () {
      this.$confirm('Start the same build again?', 'Warning', {
        confirmButtonText: 'Restart',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => this.$store.dispatch('restartBuild', this.build))
        .then(() => {
          this.restarting = true
          this.$notify({
            title: 'Put back in the queue',
            message: 'Thank you for your patience',
            type: 'success'
          })
        }).catch(() => {})
    },
    updateTimer () {
      this.now = moment().unix()
      this.timer = setInterval(() => this.now++, 1000)
    }
  },
  watch: {
    'build.state' (value) {
      if (value === 'running') {
        this.updateTimer()
      } else {
        clearInterval(this.timer)
        this.timer = null
      }
    }
  }
}
</script>

<style lang="scss">
  @import "../../theme";

  #build-item {
    border-left: 10px solid transparent;

    .actions {
      padding: 10px;
      text-align: right;

      .el-button {
        margin-bottom: 9px;
      }
    }

    .state, .info {
      padding: 10px;
      min-height: 160px;

      i {
        color: $--color-text-gray;
        font-size: 18px;
        position: absolute;
        top: 3px;
      }
      h3 {
        margin-top: 0;
      }
      td {
        border: none;
        padding-top: 3px;
        padding-bottom: 3px;
      }
      tr:first-child {
        i {
          margin-top: 2px;
        }
        &:hover {
          td {
            background-color: transparent;
          }
        }
      }
      .cell {
        padding: 0;
      }

      &::after, &::before {
        background-color: transparent;
      }
    }

    &.passed {
      border-color: $--border-color-build-success;

      h3, .title, .el-icon-circle-check {
        color: $--color-build-success;

        .el-button {
          color: $--color-build-success;
        }
      }
    }
    &.pending {
      border-color: $--border-color-build-pending;

      h3, .title, .el-icon-loading {
        color: $--color-build-pending;

        .el-button {
          color: $--color-build-pending;
        }
      }
    }
    &.running {
      border-color: $--border-color-build-running;

      h3, .title, .el-icon-loading {
        color: $--color-build-running;

        .el-button {
          color: $--color-build-running;
        }
      }
    }
    &.failed {
      border-color: $--border-color-build-error;

      h3, .title, .el-icon-error {
        color: $--color-build-error;

        .el-button {
          color: $--color-build-error;
        }
      }
    }

    &.summary {
      .state, .info {
        min-height: 1px;
      }
    }
  }
</style>
